import { useHeaderTitle } from '@blend-ed/blendx-platform';
import { getFeatureToggle, withParams } from '@blend-ed/blendx-platform/utils';
import { Accordion, Button, Spinner } from "@blend-ed/blendx-ui";
import { getConfig } from '@edx/frontend-platform';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import { useMemo, useState } from 'react';
import CourseExploreCard from '../../components/CourseExploreCard';
import useFetchCourseDetails from '../../hooks/courses/useFetchCourseDetails';
import { useCourseById, useEnrolledCourses } from "../../hooks/courses/useFetchCourses";
import useFetchCourseOutline from '../../hooks/useFetchCourseOutline';
import { getCourseOutline } from '../../utils/getCourseOutline';
import useHandleCart from '../../utils/useHandleCart';
import messages from './messages';
import TopCourses from "./top-courses";

function CourseSingle(props) {
    const { params, intl } = props
    const { data: courseOutlineData, isLoading: courseOutlineLoading } = useFetchCourseOutline(params.id)
    const { course: courseById, loading: courseByIdLoading } = useCourseById(params.id)
    const { data: courseDetails, isLoading: courseDetailsLoading } = useFetchCourseDetails(params.id)
    const { enrolledCourses } = useEnrolledCourses()
    const { handleCoursesCart, coursesInCart, coursesInWishlist } = useHandleCart()
    const { setTitle } = useHeaderTitle()
    const [showFullOverview, setShowFullOverview] = useState(false);
    const { features_show_course_overview } = getFeatureToggle()

    const alreadyEnrolled = enrolledCourses?.some(course => course.course.openedxCourseId === params.id)

    const courseData = useMemo(() => {
        if (!courseById?.details) {
            return {
                description: "",
                title: "",
                image: "",
                estimatedTime: "",
                price: "",
                inviteOnly: false,
                published: true,
                startDate: ""
            }
        }
        setTitle(courseById.details.displayName || '')
        return {
            description: courseById.details.shortDescription,
            title: courseById.details.displayName,
            image: `${getConfig().LMS_BASE_URL}${courseById.details.courseImageUrl}`,
            estimatedTime: courseById.details.effort,
            price: courseById.price,
            inviteOnly: courseById.inviteOnly,
            published: courseById.published,
            id: courseById.id,
            overview: courseDetails?.overview || "",
            startDate: courseById.details.start
        }
    }, [courseById, courseDetails])

    const sectionsSubSections = useMemo(() => {
        if (!courseOutlineData) return []
        return getCourseOutline(courseOutlineData)
    }, [courseOutlineData])

    const handleCart = () => {
        if (coursesInCart.includes(courseData.id)) {
            handleCoursesCart(courseData.id, 'delete', '')
        } else {
            handleCoursesCart(courseData.id, 'insert', 'draft')
        }
    }

    const handleWishlist = () => {
        if (coursesInWishlist.includes(courseData.id)) {
            handleCoursesCart(courseData.id, 'delete', '')
        } else {
            handleCoursesCart(courseData.id, 'insert', 'wishlist')
        }
    }

    // Show loading state while either course or outline is loading
    if (courseByIdLoading || courseOutlineLoading || courseDetailsLoading) {
        return (
            <main className='course-single__wrapper'>
                <div className="blendx-spinner-container">
                    <Spinner animation="border" size="lg" variant="dark" />
                </div>
            </main>
        )
    }

    // Check for unpublished/non-existent course after loading is complete
    if (!courseById) {
        setTitle('')
        return (
            <main className='course-single__wrapper'>
                <p>{intl.formatMessage(messages.noSuchCourse)}</p>
            </main>
        )
    }

    return (
        <main className='course-single__wrapper'>
            <div className="course-single__left">
                <div className="course-single__description-wrapper">
                    {/* Short Description Section */}
                    <div className="course-single__description">
                        <h3 className='course-single__description-title'>{intl.formatMessage(messages.descriptionTitle)}</h3>
                        <p className='course-single__description-text'>
                            {courseData.description ? courseData.description : intl.formatMessage(messages.noDescriptionFound)}
                        </p>
                    </div>

                    {/* Overview Section (HTML Content) - Only show if config is enabled */}
                    {features_show_course_overview && courseData.overview && (
                        <div className="course-single__overview">
                            <h3 className='course-single__description-title'>{intl.formatMessage(messages.overviewTitle)}</h3>
                            <div className="course-single__description-container">
                                <div
                                    className={`course-single__description-text ${!showFullOverview ? 'collapsed' : ''}`}
                                    dangerouslySetInnerHTML={{ __html: courseData.overview }}
                                />
                                <div className="course-single__description-button-wrapper">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowFullOverview(!showFullOverview)}
                                        className="course-single__description-toggle"
                                    >
                                        {showFullOverview ? intl.formatMessage(messages.showLess) : intl.formatMessage(messages.showMore)}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Course Content Section */}
                    <div className="course-single__content">
                        <h3 className='course-single__description-title'>{intl.formatMessage(messages.contentsTitle)}</h3>
                        {sectionsSubSections.length === 0 &&
                            <div className="course-single__content-empty">
                                <p className="course-single__description-text">
                                    {intl.formatMessage(messages.noContentsFound)}
                                </p>
                            </div>
                        }
                        {sectionsSubSections.map((section, index) => (
                            <Accordion key={index} className="course-single__accordion" icon="info" title={section.sectionName} activeKey={index} dropDownArrow="left">
                                {section.subSections.map((subSection, subIndex) => (
                                    <div key={subIndex}>• {subSection}</div>
                                ))}
                            </Accordion>
                        ))}
                    </div>
                </div>
                <TopCourses curr_course_id={params.id} />
            </div>
            <div className="course-single__right">
                <CourseExploreCard
                    preview={courseData.image}
                    price={courseData.price}
                    estimatedTime={courseData.estimatedTime ? courseData.estimatedTime : ""}
                    cardType="course"
                    primaryOnClick={() => handleCart()}
                    primaryBtnText={coursesInCart.includes(courseData.id) ? intl.formatMessage(messages.removeFromCart) : intl.formatMessage(messages.addToCart)}
                    secondaryOnClick={() => handleWishlist()}
                    secondaryBtnText={coursesInWishlist.includes(courseData.id) ? intl.formatMessage(messages.removeFromWishlist) : intl.formatMessage(messages.addToWishlist)}
                    inviteOnly={courseData.inviteOnly}
                    alreadyEnrolled={alreadyEnrolled}
                    onDiscoverPage={true}
                    published={courseData.published}
                    startDate={courseData.startDate}
                    title={courseData.title}
                />
            </div>
        </main>
    )
}

CourseSingle.propTypes = {
    intl: intlShape.isRequired,
};

export default injectIntl(withParams(CourseSingle));